import React, { useState } from 'react';
import { Palette, X, Check, RotateCcw } from 'lucide-react';
import { FONT_OPTIONS, THEME_PRESETS, ACCENT_SWATCHES, RADIUS_OPTIONS, applyTheme } from '../utils/theme';
import { defaultTheme } from '../utils/storage';

function SectionLabel({ children }) {
  return <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.16em', fontFamily: 'var(--mono)', margin: '0 0 10px' }}>{children}</p>;
}

export default function ThemePanel({ theme, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(theme);

  function update(patch) {
    const next = { ...draft, ...patch };
    setDraft(next);
    applyTheme(next);
  }

  function applyPreset(preset) {
    setDraft(preset.theme);
    applyTheme(preset.theme);
  }

  function handleSave() {
    onSave(draft);
    setOpen(false);
  }

  function handleReset() {
    const def = defaultTheme();
    setDraft(def);
    applyTheme(def);
  }

  function handleClose() {
    // Revert to saved theme on cancel
    applyTheme(theme);
    setDraft(theme);
    setOpen(false);
  }

  return (
    <>
      {/* Trigger button in navbar */}
      <button
        onClick={() => { setDraft(theme); setOpen(true); }}
        title="Appearance settings"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 'var(--radius)',
          border: '1px solid var(--border2)', background: 'var(--bg3)',
          color: 'var(--text2)', cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <Palette size={13} />
        <span className="appearance-label">Appearance</span>
      </button>

      {/* Side drawer */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }} onClick={e => e.target === e.currentTarget && handleClose()}>
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} onClick={handleClose} />

          {/* Panel */}
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 360,
            background: 'var(--bg2)', borderLeft: '1px solid var(--border2)',
            display: 'flex', flexDirection: 'column', overflowY: 'auto',
            animation: 'slideIn 0.22s ease',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Palette size={16} color="var(--accent)" />
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>Appearance</span>
              </div>
              <button onClick={handleClose} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 5, cursor: 'pointer', color: 'var(--text3)', display: 'flex' }}>
                <X size={15} />
              </button>
            </div>

            {/* Scrollable content */}
            <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 28 }}>

              {/* Presets */}
              <div>
                <SectionLabel>Presets</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  {THEME_PRESETS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      title={p.label}
                      style={{
                        border: draft.accent === p.theme.accent && draft.bg === p.theme.bg ? '2px solid var(--accent)' : '1px solid var(--border2)',
                        borderRadius: 'var(--radius)', padding: '8px 6px', cursor: 'pointer', background: p.theme.bg2,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        transition: 'transform 0.1s',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 3 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.theme.bg }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.theme.accent }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 500, color: p.theme.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent colour */}
              <div>
                <SectionLabel>Accent colour</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 12 }}>
                  {ACCENT_SWATCHES.map(s => (
                    <button
                      key={s.color}
                      onClick={() => update({ accent: s.color })}
                      title={s.label}
                      style={{
                        width: '100%', aspectRatio: '1', borderRadius: 'var(--radius)', background: s.color,
                        border: draft.accent === s.color ? '3px solid var(--text)' : '2px solid transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 0.1s',
                      }}
                    >
                      {draft.accent === s.color && <Check size={12} color="#000" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
                {/* Custom hex input */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius)', background: draft.accent, border: '1px solid var(--border2)', flexShrink: 0 }} />
                  <input
                    type="color"
                    value={draft.accent}
                    onChange={e => update({ accent: e.target.value })}
                    style={{ width: 36, height: 32, borderRadius: 'var(--radius)', border: '1px solid var(--border2)', padding: 2, cursor: 'pointer', background: 'var(--bg3)' }}
                  />
                  <input
                    value={draft.accent}
                    onChange={e => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && update({ accent: e.target.value })}
                    style={{ flex: 1, padding: '7px 10px', fontSize: 12, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', color: 'var(--text)', fontFamily: 'var(--mono)' }}
                    placeholder="#f0c040"
                  />
                </div>
              </div>

              {/* Background mode */}
              <div>
                <SectionLabel>Mode</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { id: 'dark',  label: 'Dark',  bg: '#0a0a0a', bg2: '#111111', bg3: '#1a1a1a', bg4: '#222222' },
                    { id: 'light', label: 'Light', bg: '#f8f8f6', bg2: '#ffffff', bg3: '#f1f0ec', bg4: '#e8e7e2' },
                  ].map(m => (
                    <button
                      key={m.id}
                      onClick={() => update({ mode: m.id, bg: m.bg, bg2: m.bg2, bg3: m.bg3, bg4: m.bg4 })}
                      style={{
                        padding: '10px', borderRadius: 'var(--radius)', cursor: 'pointer',
                        border: draft.mode === m.id ? '2px solid var(--accent)' : '1px solid var(--border2)',
                        background: m.bg2, display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <div style={{ width: 20, height: 20, borderRadius: 'var(--radius)', background: m.bg }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: m.id === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background colour (custom) */}
              <div>
                <SectionLabel>Background colour</SectionLabel>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={draft.bg}
                    onChange={e => {
                      const bg = e.target.value;
                      // Auto-derive bg2/bg3/bg4 from bg
                      const bg2 = lighten(bg, draft.mode === 'dark' ? 6 : -4);
                      const bg3 = lighten(bg, draft.mode === 'dark' ? 12 : -8);
                      const bg4 = lighten(bg, draft.mode === 'dark' ? 18 : -12);
                      update({ bg, bg2, bg3, bg4 });
                    }}
                    style={{ width: 36, height: 32, borderRadius: 'var(--radius)', border: '1px solid var(--border2)', padding: 2, cursor: 'pointer', background: 'var(--bg3)' }}
                  />
                  <input
                    value={draft.bg}
                    onChange={e => {
                      if (!/^#[0-9a-fA-F]{6}$/.test(e.target.value)) return;
                      update({ bg: e.target.value });
                    }}
                    style={{ flex: 1, padding: '7px 10px', fontSize: 12, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', color: 'var(--text)', fontFamily: 'var(--mono)' }}
                    placeholder="#0a0a0a"
                  />
                </div>
                <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Card surfaces are auto-derived from background.</p>
              </div>

              {/* Font */}
              <div>
                <SectionLabel>Font</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {FONT_OPTIONS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => update({ font: f.id })}
                      style={{
                        padding: '9px 12px', borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'left',
                        border: draft.font === f.id ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                        background: draft.font === f.id ? 'rgba(var(--accent-rgb, 240,192,64),0.08)' : 'var(--bg3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontSize: 14, fontFamily: `'${f.id}', sans-serif`, color: 'var(--text)', fontWeight: draft.font === f.id ? 500 : 400 }}>{f.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: `'${f.id}', sans-serif` }}>Aa Bb 123</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Border radius */}
              <div>
                <SectionLabel>Corner radius</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  {RADIUS_OPTIONS.map(r => (
                    <button
                      key={r.id}
                      onClick={() => update({ borderRadius: r.id })}
                      style={{
                        padding: '10px 8px', cursor: 'pointer',
                        border: draft.borderRadius === r.id ? '2px solid var(--accent)' : '1px solid var(--border2)',
                        background: 'var(--bg3)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        borderRadius: 'var(--radius)',
                      }}
                    >
                      <div style={{ width: 28, height: 20, background: 'var(--bg4)', border: '1px solid var(--border2)', borderRadius: r.id }} />
                      <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text3)' }}>{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, background: 'var(--bg2)', position: 'sticky', bottom: 0 }}>
              <button
                onClick={handleReset}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text3)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '7px 10px', borderRadius: 'var(--radius)' }}
              >
                <RotateCcw size={12} /> Reset
              </button>
              <div style={{ flex: 1 }} />
              <button onClick={handleClose} style={{ fontSize: 13, fontWeight: 500, padding: '8px 16px', borderRadius: 'var(--radius)', background: 'var(--bg3)', border: '1px solid var(--border2)', color: 'var(--text2)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} style={{ fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 'var(--radius)', background: 'var(--accent)', border: 'none', color: 'var(--bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={13} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}

// lighten/darken a hex by a percentage
function lighten(hex, pct) {
  const n = parseInt(hex.replace('#',''), 16);
  const clamp = v => Math.min(255, Math.max(0, v));
  const r = clamp(((n >> 16) & 0xff) + pct * 2.5) | 0;
  const g = clamp(((n >> 8)  & 0xff) + pct * 2.5) | 0;
  const b = clamp(((n)        & 0xff) + pct * 2.5) | 0;
  return '#' + [r,g,b].map(x => x.toString(16).padStart(2,'0')).join('');
}
